import { defineStore } from 'pinia';
import { computed, reactive, ref, type Ref } from 'vue';
import {
    type PublicClientApplication,
    InteractionStatus,
    type AccountInfo,
    EventMessageUtils,
    type EventMessage,
    EventType,
    InteractionType,
    type AuthenticationResult,
    type EventCallbackFunction,
    type CacheLookupPolicy,
} from '@azure/msal-browser';
import { until } from '@vueuse/core';
import { accountArraysAreEqual } from '../utils/comparison';
import {
    getAccount,
    type AccountSelectorInput,
    type MaybeAccountRef,
    resolveAccountUnref,
} from '../utils/accountSelectors';

export type LoginOptions = {
    scopes?: string[];
    extraScopes?: string[];
    interactionType?: InteractionType.Redirect | InteractionType.Popup;
    extraQueryParameters?: Record<string, string>;
    authority?: string;
    account?: MaybeAccountRef;
    skipWaitTillIdle?: boolean;
    redirectStartPage?: string;
};

type NormalizedMsalStoreOptions = {
    login: {
        scopes: string[];
        defaultInteractionType:
            | InteractionType.Redirect
            | InteractionType.Popup;
        extraQueryParameters: Record<string, string>;
    };
    passwordReset: {
        authority: string | null;
        defaultInteractionType:
            | InteractionType.Redirect
            | InteractionType.Popup;
    };
    profileEdit: {
        authority: string | null;
        defaultInteractionType:
            | InteractionType.Redirect
            | InteractionType.Popup;
    };
    logout: {
        postLogoutRedirectUri: string;
        defaultInteractionType:
            | InteractionType.Redirect
            | InteractionType.Popup;
    };
    aquireToken: {
        defaultScopes: string[];
        defaultInteractionType:
            | InteractionType.Redirect
            | InteractionType.Popup;
    };
    activeAccountSelector: AccountSelectorInput;
    autoselectActiveAccount: boolean;
    /**
     * The maximum amount of time to initialize the msal client. After this time, an
     * error is thrown stating that the msal package should be installed.
     *
     * You may provide 0 to allow infinite waiting on the msal instance.
     *
     * @default 200 (ms)
     */
    msalInitialisationTimeout: number;
    /**
     * @default 2000 (ms)
     */
    interactionStatusIdleTimeout: number;
};

export type MsalStoreOptions = Partial<NormalizedMsalStoreOptions>;

export const useMsalStore = defineStore('msal', () => {
    // Main state.
    const msalInstance: Ref<PublicClientApplication | null> = ref(null);
    let msalInitPromise: Promise<void> | null = null;

    const interactionStatus = ref<InteractionStatus>(InteractionStatus.Startup);
    const accounts = ref<AccountInfo[]>([]);

    const eventCallbacks = new Map<EventCallbackFunction, string | null>();

    const loginFailureMessage = ref<EventMessage | null>(null);
    const logoutFailureMessage = ref<EventMessage | null>(null);

    // Options
    const options = reactive<NormalizedMsalStoreOptions>({
        login: {
            scopes: ['openid'],
            defaultInteractionType: InteractionType.Redirect,
            extraQueryParameters: {},
        },
        passwordReset: {
            authority: null,
            defaultInteractionType: InteractionType.Redirect,
        },
        profileEdit: {
            authority: null,
            defaultInteractionType: InteractionType.Redirect,
        },
        logout: {
            postLogoutRedirectUri: '/',
            defaultInteractionType: InteractionType.Redirect,
        },
        aquireToken: {
            defaultScopes: ['openid'],
            defaultInteractionType: InteractionType.Redirect,
        },
        activeAccountSelector: null,
        autoselectActiveAccount: true,
        msalInitialisationTimeout: 200,
        interactionStatusIdleTimeout: 2000,
    });

    function setDefaultInteractionType(
        interactionType: InteractionType.Redirect | InteractionType.Popup
    ) {
        options.login.defaultInteractionType = interactionType;
        options.logout.defaultInteractionType = interactionType;
        options.aquireToken.defaultInteractionType = interactionType;
        options.profileEdit.defaultInteractionType = interactionType;
        options.passwordReset.defaultInteractionType = interactionType;
    }

    function addLoginScopes(...scopes: string[]) {
        options.login.scopes = [
            ...new Set([...options.login.scopes, ...scopes]),
        ];
    }

    function addLoginQueryParameters(parameters: Record<string, string>) {
        options.login.extraQueryParameters = {
            ...options.login.extraQueryParameters,
            ...parameters,
        };
    }

    function setPasswordResetAuthority(authority: string) {
        options.passwordReset.authority = authority;
    }

    function setProfileEditAuthority(authority: string) {
        options.profileEdit.authority = authority;
    }

    // Derived state
    const loading = computed<boolean>(() => {
        switch (interactionStatus.value) {
            case InteractionStatus.Login:
            case InteractionStatus.Startup:
            case InteractionStatus.HandleRedirect:
            case InteractionStatus.SsoSilent:
                return true;
            default:
                return false;
        }
    });

    const loginLoading = computed<boolean>(
        () => interactionStatus.value === InteractionStatus.Login
    );
    const logoutLoading = computed<boolean>(
        () => interactionStatus.value === InteractionStatus.Logout
    );
    const aquireTokenLoading = computed<boolean>(
        () => interactionStatus.value === InteractionStatus.AcquireToken
    );

    const idle = computed<boolean>(
        () => interactionStatus.value === InteractionStatus.None
    );

    // Redirect Promises
    const redirectPromisesHandled = ref<boolean>(false);

    function startHandlingRedirectPromises() {
        msalInstance.value?.handleRedirectPromise().catch(() => {
            return;
        });
        redirectPromisesHandled.value = true;
    }

    function handleRedirectPromise() {
        if (!redirectPromisesHandled.value) startHandlingRedirectPromises();
    }

    // Active account
    const activeAccountInternal = ref<AccountInfo | null>(null);

    const activeAccount = computed({
        get: () =>
            activeAccountInternal.value ??
            msalInstance.value?.getActiveAccount() ??
            null,
        set: (newValue) => {
            activeAccountInternal.value = newValue;
            if (msalInstance.value) {
                msalInstance.value.setActiveAccount(newValue);
            }
        },
    });

    function refreshActiveAccount() {
        if (!msalInstance.value) return;
        activeAccount.value = getAccount(
            msalInstance.value,
            options.activeAccountSelector
        );
    }

    function addEventCallback(callback: EventCallbackFunction) {
        if (eventCallbacks.has(callback)) return;

        const msal = msalInstance.value;
        if (msal) {
            const callbackRef = msal.addEventCallback(callback);
            eventCallbacks.set(callback, callbackRef);
        } else {
            eventCallbacks.set(callback, null);
        }
    }

    function removeEventCallback(callback: EventCallbackFunction) {
        const callbackRef = eventCallbacks.get(callback);
        const msal = msalInstance.value;
        if (typeof callbackRef === 'string' && msal) {
            msal.removeEventCallback(callbackRef);
        }
        eventCallbacks.delete(callback);
    }

    // Changing Msal instances.
    let eventCallback: string | null = null;

    function unsetMsalInstance() {
        const currentMsalInstance = msalInstance.value;

        if (currentMsalInstance === null) return;

        if (eventCallback)
            currentMsalInstance.removeEventCallback(eventCallback);

        for (const [callback, callbackRef] of [...eventCallbacks]) {
            if (typeof callbackRef === 'string') {
                currentMsalInstance.removeEventCallback(callbackRef);
                eventCallbacks.set(callback, null);
            }
        }

        msalInstance.value = null;
        msalInitPromise = null;
        loginFailureMessage.value = null;
        logoutFailureMessage.value = null;
    }

    async function setMsalInstance(instance: PublicClientApplication) {
        unsetMsalInstance();

        for (const callback of [...eventCallbacks.keys()]) {
            const callbackRef = instance.addEventCallback(callback);
            eventCallbacks.set(callback, callbackRef);
        }

        eventCallback = instance.addEventCallback((message: EventMessage) => {
            switch (message.eventType) {
                case EventType.ACCOUNT_ADDED:
                case EventType.ACCOUNT_REMOVED:
                case EventType.LOGIN_SUCCESS:
                case EventType.SSO_SILENT_SUCCESS:
                case EventType.HANDLE_REDIRECT_END:
                case EventType.LOGIN_FAILURE:
                case EventType.SSO_SILENT_FAILURE:
                case EventType.LOGOUT_END:
                case EventType.ACQUIRE_TOKEN_SUCCESS:
                case EventType.ACQUIRE_TOKEN_FAILURE: {
                    const currentAccounts = instance.getAllAccounts();
                    if (
                        !accountArraysAreEqual(currentAccounts, accounts.value)
                    ) {
                        accounts.value = currentAccounts;
                        if (options.autoselectActiveAccount)
                            refreshActiveAccount();
                    }
                    break;
                }
            }

            const status = EventMessageUtils.getInteractionStatusFromEvent(
                message,
                interactionStatus.value
            );
            if (status !== null) {
                interactionStatus.value = status;
            }

            switch (message.eventType) {
                case EventType.LOGIN_FAILURE:
                    loginFailureMessage.value = message;
                    break;
                case EventType.LOGIN_START:
                case EventType.LOGIN_SUCCESS:
                    loginFailureMessage.value = null;
                    break;
            }

            switch (message.eventType) {
                case EventType.LOGOUT_FAILURE:
                    logoutFailureMessage.value = message;
                    break;
                case EventType.LOGOUT_START:
                case EventType.LOGOUT_SUCCESS:
                    logoutFailureMessage.value = message;
                    break;
            }

            if (
                message.eventType === EventType.LOGIN_FAILURE &&
                message.error &&
                'errorMessage' in message.error &&
                message.error.errorMessage.indexOf('AADB2C90118') > -1 &&
                options.passwordReset.authority !== null
            ) {
                passwordReset({
                    skipWaitTillIdle: true,
                });
            }

            if (message.eventType === EventType.LOGIN_SUCCESS) {
                const payload = message.payload as AuthenticationResult;
                const claims = payload.idTokenClaims;

                if ('tfp' in claims && typeof claims.tfp === 'string') {
                    const tfp = claims.tfp.toLowerCase();

                    switch (tfp) {
                        case 'b2c_1a_passwordreset': {
                            const extraQueryParameters: Record<string, string> =
                                {
                                    prompt: 'login',
                                };

                            if (
                                'email' in claims &&
                                typeof claims.email === 'string'
                            ) {
                                extraQueryParameters['login_hint'] =
                                    claims.email;
                            }

                            login({ extraQueryParameters });
                            break;
                        }
                        case 'b2c_1a_profileedit':
                            login();
                            break;
                        default:
                            break;
                    }
                }
            }
        });

        // Sets the msal instance.
        msalInstance.value = instance;
        msalInitPromise = instance.initialize();

        msalInitPromise.then(() => {
            // Load configuration.
            const configuration = instance.getConfiguration();
            if (configuration.auth.postLogoutRedirectUri) {
                options.logout.postLogoutRedirectUri =
                    configuration.auth.postLogoutRedirectUri;
            }

            // Sets the active account.
            const newActiveAccount = instance.getActiveAccount();
            if (newActiveAccount) {
                activeAccountInternal.value = newActiveAccount;
            } else if (activeAccountInternal.value) {
                instance.setActiveAccount(activeAccountInternal.value);
            }

            // Re-trigger the redirect promises handed.
            if (redirectPromisesHandled.value) startHandlingRedirectPromises();
        });
    }

    async function getMsalInstance(): Promise<PublicClientApplication> {
        if (msalInstance.value !== null) return msalInstance.value;

        const timeout = options.msalInitialisationTimeout;
        await until(msalInstance)
            .not.toBeNull({
                timeout,
                throwOnTimeout: true,
            })
            .catch((e) => {
                throw new Error(
                    `Msal instance not initialized within ${timeout} ms.`,
                    { cause: e }
                );
            });

        if (msalInstance.value === null)
            throw Error('MsalInstance still null!');

        if (!msalInitPromise) {
            throw new Error('MsalInitPromise not set');
        }

        await msalInitPromise;

        return msalInstance.value;
    }

    const msal = computed<PublicClientApplication | null>({
        get: () => msalInstance.value,
        set: (newValue) => {
            if (newValue) {
                setMsalInstance(newValue);
            } else {
                unsetMsalInstance();
            }
        },
    });

    // ACTIONS

    async function waitTillIdle(timeout?: number): Promise<void> {
        if (idle.value) return;
        timeout ??= options.interactionStatusIdleTimeout;
        await until(idle)
            .toBeTruthy({
                timeout,
                throwOnTimeout: true,
            })
            .catch((e) => {
                throw new Error(
                    `Msal InteractionStatus did not become idle within ${timeout} ms.`,
                    { cause: e }
                );
            });
    }

    async function ensureAuthenticated(
        opts: LoginOptions = {}
    ): Promise<AccountInfo | null> {
        handleRedirectPromise();
        const msal = await getMsalInstance();
        if (!opts.skipWaitTillIdle) await waitTillIdle();

        const account = resolveAccountUnref(msal, opts.account);
        if (!account && !loginFailureMessage.value) {
            await login(opts);
        }
        return resolveAccountUnref(msalInstance.value, opts.account);
    }

    // Login
    async function login(
        opts: LoginOptions = {}
    ): Promise<AuthenticationResult> {
        const scopes = [
            ...(opts.scopes ?? options.login.scopes),
            ...(opts.extraScopes ?? []),
        ];
        const interactionType =
            opts.interactionType ?? options.login.defaultInteractionType;
        const extraQueryParameters = {
            ...options.login.extraQueryParameters,
            ...(opts.extraQueryParameters ?? {}),
        };
        const authority = opts.authority;
        const account =
            resolveAccountUnref(msalInstance.value, opts.account) ?? undefined;

        const msal = await getMsalInstance();
        if (!opts.skipWaitTillIdle) await waitTillIdle();

        switch (interactionType) {
            case InteractionType.Popup:
                return msal.loginPopup({
                    scopes,
                    extraQueryParameters,
                    authority,
                    account,
                });
            default:
            case InteractionType.Redirect:
                await msal.loginRedirect({
                    scopes,
                    extraQueryParameters,
                    authority,
                    account,
                    redirectStartPage: opts.redirectStartPage,
                });
                return undefined as never;
        }
    }

    // ProfileEdit
    const profileEditAvailable = computed(
        () =>
            msalInstance.value !== null &&
            options.profileEdit.authority !== null &&
            idle.value
    );

    async function profileEdit(
        opts: {
            interactionType?: InteractionType.Redirect | InteractionType.Popup;
            extraQueryParameters?: Record<string, string>;
            account?: MaybeAccountRef;
        } = {}
    ): Promise<AuthenticationResult> {
        const interactionType =
            opts.interactionType ?? options.profileEdit.defaultInteractionType;
        const authority = options.profileEdit.authority;
        if (!authority) {
            throw new Error(
                'Cannot perform profileEdit becuase the `profileEdit`-authority has not been set in the vue-msal-store!'
            );
        }

        return await login({
            ...opts,
            authority,
            interactionType,
        });
    }

    // Password Reset
    const passwordResetAvailable = computed(
        () =>
            msalInstance.value !== null &&
            options.passwordReset.authority !== null
    );

    async function passwordReset(
        opts: {
            interactionType?: InteractionType.Redirect | InteractionType.Popup;
            extraQueryParameters?: Record<string, string>;
            account?: MaybeAccountRef;
            skipWaitTillIdle?: boolean;
        } = {}
    ): Promise<AuthenticationResult> {
        const interactionType =
            opts.interactionType ??
            options.passwordReset.defaultInteractionType;
        const authority = options.passwordReset.authority;
        if (!authority) {
            throw new Error(
                'Cannot perform profileEdit becuase the `profileEdit`-authority has not been set in the vue-msal-store!'
            );
        }

        return await login({
            ...opts,
            authority,
            interactionType,
        });
    }

    // Logout
    async function logout(
        opts: {
            postLogoutRedirectUri?: string;
            interactionType?: InteractionType.Redirect | InteractionType.Popup;
            account?: AccountInfo | null;
        } = {}
    ): Promise<void> {
        const redirectUri =
            opts.postLogoutRedirectUri ?? options.logout.postLogoutRedirectUri;
        const interactionType =
            opts.interactionType ?? options.logout.defaultInteractionType;

        const msal = await getMsalInstance();
        await waitTillIdle();

        switch (interactionType) {
            case InteractionType.Popup:
                return await msal.logoutPopup({
                    postLogoutRedirectUri: redirectUri,
                    account: opts.account,
                });
            default:
            case InteractionType.Redirect:
                return await msal.logoutRedirect({
                    postLogoutRedirectUri: redirectUri,
                    account: opts.account,
                });
        }
    }

    // Aquire access tokens.
    async function acquireToken(
        opts: {
            scopes?: string[];
            interactionType?: InteractionType.Redirect | InteractionType.Popup;
            extraLoginQueryParameters?: Record<string, string>;
            account?: AccountInfo;
            cacheLookupPolicy?: CacheLookupPolicy;
        } = {}
    ): Promise<AuthenticationResult> {
        const scopes = opts.scopes ?? options.aquireToken.defaultScopes;
        const interactionType =
            opts.interactionType ?? options.aquireToken.defaultInteractionType;

        const msal = await getMsalInstance();

        // Handle the case where msal was redirected.
        switch (interactionStatus.value) {
            case InteractionStatus.Startup:
            case InteractionStatus.HandleRedirect: {
                const response = await msal.handleRedirectPromise();
                if (response) return response;
            }
        }

        // Try to make a silent token request.
        try {
            const resp = await msal.acquireTokenSilent({
                scopes,
                account: opts.account ?? activeAccount.value ?? undefined,
                cacheLookupPolicy: opts.cacheLookupPolicy,
            });
            return resp;
        } catch (e) {
            console.warn('Could not aquire token.', e);
            if (loginFailureMessage.value) {
                throw e;
            } else {
                return await login({
                    scopes,
                    interactionType,
                    extraQueryParameters: opts.extraLoginQueryParameters,
                });
            }
        }
    }

    return {
        msal,
        interactionStatus,
        accounts,
        activeAccount,
        refreshActiveAccount,
        setMsalInstance,
        loading,
        handleRedirectPromise,
        acquireToken,
        login,
        addLoginScopes,
        loginLoading,
        loginFailureMessage,
        logout,
        logoutLoading,
        logoutFailureMessage,
        profileEdit,
        profileEditAvailable,
        passwordReset,
        passwordResetAvailable,
        setDefaultInteractionType,
        idle,
        waitTillIdle,
        aquireTokenLoading,
        setPasswordResetAuthority,
        setProfileEditAuthority,
        addEventCallback,
        removeEventCallback,
        addLoginQueryParameters,
        ensureAuthenticated,
    };
});
